import {
  BusinessFilter,
  DashboardStatsPeriod,
  QueueCustomRange,
  QueuePeriod,
  RequestChannel,
  RequestStatus,
  TeamChannel,
} from "@/models/underwriting";

const MS_PER_DAY = 86_400_000;

function toDateOnly(date: Date): string {
  return date.toLocaleDateString("en-CA");
}

function atMidday(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(12, 0, 0, 0);
  return copy;
}

function addDays(date: Date, days: number): Date {
  const copy = atMidday(date);
  copy.setTime(copy.getTime() + days * MS_PER_DAY);
  return copy;
}

function startOfIsoWeek(date: Date): Date {
  const copy = atMidday(date);
  const day = copy.getDay();
  const diff = day === 0 ? 6 : day - 1; // Monday
  return addDays(copy, -diff);
}

/** Resolve a dashboard stats period into an inclusive [start, end] date range (YYYY-MM-DD). */
export function computeDashboardPeriodRange(period: DashboardStatsPeriod): { start: string; end: string } {
  const todayDate = atMidday(new Date());
  const today = toDateOnly(todayDate);

  if (period === "Today") return { start: today, end: today };

  if (period === "LastWeek") {
    const thisWeekStart = startOfIsoWeek(todayDate);
    const lastWeekStart = addDays(thisWeekStart, -7);
    const lastWeekEnd = addDays(thisWeekStart, -1);
    return { start: toDateOnly(lastWeekStart), end: toDateOnly(lastWeekEnd) };
  }

  const lastMonthStart = new Date(todayDate.getFullYear(), todayDate.getMonth() - 1, 1);
  const lastMonthEnd = new Date(todayDate.getFullYear(), todayDate.getMonth(), 0);
  return { start: toDateOnly(lastMonthStart), end: toDateOnly(lastMonthEnd) };
}

/** Resolve a queue period into an inclusive [start, end] date range (YYYY-MM-DD). */
export function computeQueuePeriodRange(
  period: QueuePeriod,
  customRange?: QueueCustomRange | null
): { start: string; end: string } {
  if (period === "Custom" && customRange?.start && customRange?.end) {
    return customRange;
  }

  const todayDate = atMidday(new Date());
  const windowDays = period === "Today" ? 1 : period === "Last30Days" ? 30 : 7;
  const start = addDays(todayDate, -(windowDays - 1));
  return { start: toDateOnly(start), end: toDateOnly(todayDate) };
}

/** Map the top-level Business filter to a request channel (or null for "Whole Business"). */
export function businessToChannel(business: BusinessFilter): RequestChannel | null {
  return business === "Whole Business" ? null : business;
}

/** Map the top-level Business filter to a Team Performance channel value. */
export function businessToTeamChannel(business: BusinessFilter): TeamChannel | null {
  return business === "Whole Business" ? null : business;
}

export function formatUnderwritingQuoteAmount(value: number | null | undefined): string {
  return value != null
    ? `£${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";
}

/** Statuses that mean the request is still open and can be claimed/claimed over. */
const UNACTIONED_STATUSES: ReadonlySet<RequestStatus> = new Set(["Pending", "InReview"]);

export function isRequestActionable(status: RequestStatus): boolean {
  return UNACTIONED_STATUSES.has(status);
}